import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkle, DownloadSimple, Copy, Check, Trash, ShareNetwork } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const GUIDE = [
  "Non hai TikTok? Scarica l'app, registrati con l'email della struttura e passa a \"Account business\" (Profilo → ☰ → Impostazioni e privacy → Account → Passa a account business).",
  "Premi \"Crea 5 bozze\" oppure accendi le bozze automatiche del lunedì: in 2–3 minuti trovi 5 video con testo e hashtag. Se hai caricato le tue foto in \"Le mie foto\", usiamo quelle.",
  "Dal telefono premi \"Apri in TikTok\" e scegli TikTok: il video si apre direttamente nell'editor e il testo con gli hashtag è già copiato.",
  "In TikTok tocca \"Suoni\" e scegli un audio di tendenza, poi Avanti → tieni premuto nella descrizione → Incolla → Pubblica. Infine qui segna \"Pubblicato\".",
];

function Draft({ it, reload }) {
  const text = `${it.caption}\n\n${it.hashtags.map((h) => `#${h.replace(/^#/, "")}`).join(" ")}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); }
    catch {
      const ta = document.createElement("textarea"); ta.value = text; document.body.appendChild(ta); ta.select();
      const ok = document.execCommand("copy"); ta.remove();
      if (!ok) return toast.error("Copia non riuscita: tieni premuto sul testo per copiarlo");
    }
    toast.success("Testo copiato");
  };
  const posted = async () => { await api.post(`/tiktok/drafts/${it.id}/posted`); reload(); };
  const del = async () => { await api.delete(`/tiktok/drafts/${it.id}`); reload(); };
  const share = async () => {
    try {
      const blob = await (await fetch(`${API}${it.video_url}`)).blob();
      const file = new File([blob], `otelya-${it.id.slice(0, 6)}.mp4`, { type: "video/mp4" });
      if (!navigator.canShare?.({ files: [file] })) return toast.error("Apri la dashboard dal telefono per inviare il video a TikTok");
      try { await navigator.clipboard.writeText(text); } catch { /* testo copiabile a mano */ }
      await navigator.share({ files: [file], text });
    } catch (e) { if (e.name !== "AbortError") toast.error("Condivisione non riuscita"); }
  };
  return (
    <div className="rounded-2xl border border-line bg-canvas p-3 space-y-2" data-testid={`tt-draft-${it.id}`}>
      <video src={`${API}${it.video_url}`} controls playsInline className="w-full aspect-[9/16] rounded-xl bg-ink object-cover" />
      <div className="font-medium text-ink text-sm">{it.title}</div>
      <div className="text-xs text-stone whitespace-pre-line">{text}</div>
      <div className="flex flex-wrap gap-1.5">
        <button onClick={share} className="inline-flex items-center text-xs px-3 py-1.5 rounded-full bg-[#111] text-white" data-testid={`tt-share-${it.id}`}><ShareNetwork size={14} className="mr-1" />Apri in TikTok</button>
        <a href={`${API}${it.video_url}`} download className="inline-flex items-center text-xs px-3 py-1.5 rounded-full border border-line" data-testid={`tt-download-${it.id}`}><DownloadSimple size={14} className="mr-1" />Scarica</a>
        <button onClick={copy} className="inline-flex items-center text-xs px-3 py-1.5 rounded-full border border-line" data-testid={`tt-copy-${it.id}`}><Copy size={14} className="mr-1" />Copia testo</button>
        {it.status === "posted" ? <span className="text-xs text-olive inline-flex items-center"><Check size={14} className="mr-1" />Pubblicato</span>
          : <button onClick={posted} className="inline-flex items-center text-xs px-3 py-1.5 rounded-full border border-line" data-testid={`tt-posted-${it.id}`}><Check size={14} className="mr-1" />Pubblicato</button>}
        <button onClick={del} className="inline-flex items-center text-xs px-2 py-1.5 text-stone" data-testid={`tt-delete-${it.id}`}><Trash size={14} /></button>
      </div>
    </div>
  );
}

export default function TikTokStudio() {
  const [d, setD] = useState({ batch: null, items: [] });
  const [topic, setTopic] = useState("");
  const load = () => api.get("/tiktok/drafts").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  const generating = d.batch?.status === "generating";
  useEffect(() => { if (!generating) return; const t = setInterval(load, 8000); return () => clearInterval(t); }, [generating]);
  const gen = async () => { try { await api.post("/tiktok/drafts/generate", { topic }); toast.success("Preparo 5 bozze, 2–3 minuti"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };
  return (
    <div className="space-y-6" data-testid="tiktok-studio">
      <ol className="space-y-2 max-w-3xl">{GUIDE.map((t, i) => <li key={i} className="flex items-baseline gap-3 text-sm text-ink"><span className="font-serif text-xl text-gold w-6">{i + 1}</span><span>{t}</span></li>)}</ol>
      <div className="flex flex-wrap gap-2 max-w-3xl">
        <Input className="flex-1 min-w-[240px]" placeholder="Tema (facoltativo): es. colazione, weekend romantico, dietro le quinte" value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="tt-topic" />
        <Button className="rounded-full bg-ink text-canvas" disabled={generating} onClick={gen} data-testid="tt-generate-btn"><Sparkle size={16} className="mr-1" />{generating ? "Preparo le bozze…" : "Crea 5 bozze"}</Button>
      </div>
      {d.batch?.status === "failed" && <div className="text-sm text-terracotta" data-testid="tt-error">Creazione non riuscita, riprova.</div>}
      <label className="flex items-center gap-3 text-sm text-ink max-w-3xl" data-testid="tt-weekly">
        <Switch checked={!!d.weekly} onCheckedChange={async (v) => { await api.put(`/tiktok/weekly?on=${v}`); load(); }} data-testid="tt-weekly-switch" />
        Ogni lunedì mattina preparami 5 nuove bozze in automatico
      </label>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">{d.items.map((it) => <Draft key={it.id} it={it} reload={load} />)}</div>
      {!d.items.length && !generating && <div className="text-sm text-stone" data-testid="tt-empty">Nessuna bozza ancora.</div>}
    </div>
  );
}
